import { useEffect, useState } from "react";
import axios from "axios";
import * as Icons from "lucide-react";
import { PUBLIC_BASE } from "../lib/api";

/**
 * Public Q&A thread shown beneath each reel on the share page.
 * Visitors post questions; the realtor replies via the back office; replies
 * are visible to all future visitors as a mini-FAQ. Polls every 25s while
 * the page is open so a viewer who keeps the tab open sees fresh answers.
 */
export default function ReelChat({ shareToken, brand }) {
  const [messages, setMessages] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    if (!shareToken) return;
    try {
      const r = await axios.get(`${PUBLIC_BASE}/api/share/reels/${shareToken}/messages`);
      setMessages(r.data || []);
    } catch {
      // silent
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 25000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareToken]);

  const submit = async () => {
    setErr(null);
    if (!body.trim()) return;
    setSending(true);
    try {
      const r = await axios.post(
        `${PUBLIC_BASE}/api/share/reels/${shareToken}/messages`,
        {
          name: name.trim() || null,
          email: email.trim().toLowerCase() || null,
          body: body.trim(),
        }
      );
      // Optimistic append (server returns the saved doc)
      setMessages((m) => [...m, r.data]);
      setBody("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't send — try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="mt-6 bg-[#0A0A0A] border border-white/10 rounded-2xl p-5"
      data-testid="reel-chat"
    >
      <div className="flex items-center gap-2 mb-3">
        <Icons.MessageCircle size={14} style={{ color: brand || "#a1a1aa" }} />
        <div className="text-xs uppercase tracking-widest text-zinc-500">
          Questions about this reel
        </div>
        {messages.length > 0 && (
          <span className="text-[10px] font-mono text-zinc-600">
            · {messages.length}
          </span>
        )}
      </div>

      {/* Thread */}
      {messages.length > 0 ? (
        <div className="space-y-3 mb-4 max-h-72 overflow-y-auto pr-1">
          {messages.map((m) => (
            <div key={m.id} className="text-sm">
              <div className="flex items-baseline gap-2">
                <span className="font-medium text-zinc-200">
                  {m.name || "Visitor"}
                </span>
                <span className="text-[10px] text-zinc-600">
                  {fmtRelative(m.created_at)}
                </span>
              </div>
              <div className="text-zinc-300 whitespace-pre-wrap">{m.body}</div>
              {m.reply && (
                <div
                  className="mt-2 ml-4 pl-3 border-l-2 text-sm"
                  style={{ borderColor: brand || "#52525b" }}
                  data-testid="reel-chat-reply"
                >
                  <div className="flex items-baseline gap-2">
                    <span
                      className="text-[10px] uppercase tracking-widest font-medium"
                      style={{ color: brand || "#a1a1aa" }}
                    >
                      Agent reply
                    </span>
                    <span className="text-[10px] text-zinc-600">
                      {fmtRelative(m.replied_at)}
                    </span>
                  </div>
                  <div className="text-zinc-200 whitespace-pre-wrap">
                    {m.reply}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-[12px] text-zinc-600 mb-4 italic">
          No questions yet — be the first.
        </div>
      )}

      {/* Composer */}
      <div className="space-y-2.5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <input
            placeholder="Your name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30"
            data-testid="reel-chat-name"
          />
          <input
            placeholder="Email (only if you want a reply)"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30"
            data-testid="reel-chat-email"
          />
        </div>
        <textarea
          placeholder="Ask anything about this listing…"
          rows={2}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30"
          data-testid="reel-chat-body"
        />
        {err && (
          <div className="text-[11px] text-red-400">{err}</div>
        )}
        <button
          onClick={submit}
          disabled={sending || !body.trim()}
          className="text-sm font-medium rounded-lg py-2 px-4 disabled:opacity-30 inline-flex items-center gap-2 transition-all"
          style={{
            background: brand || "#ffffff",
            color: brand ? "#ffffff" : "#000000",
          }}
          data-testid="reel-chat-send"
        >
          {sending ? (
            <Icons.Loader2 size={13} className="animate-spin" />
          ) : (
            <Icons.Send size={13} />
          )}
          Post question
        </button>
      </div>
    </div>
  );
}

function fmtRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}
